import {Avatar, Button} from 'antd'
import classNames from 'classnames'
import ImageUpload from 'components/form/fields/ImageUpload'
import {createValue} from 'components/form/utils'
import ImageLoader from 'components/ImageLoader'
import {PhotoPlaceholderApp} from 'conts/imageBase64'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {BsCloudArrowUp, BsImage, BsPersonFill} from 'react-icons/bs'
import {NullAsDefault, renderSelf} from 'views/Shared'
import {PiCloudArrowUp, PiTrash} from "react-icons/pi";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import {BiTrash} from "react-icons/bi";
import {IoImage} from "react-icons/io5";
import {GoDotFill} from "react-icons/go";

const PhotoUploadWrapper = ({children}) => (
    <div className="flex flex-col relative gap-y-3">
        {children}
    </div>
)

const PhotoField = ({
                        name,
                        value,
                        values,
                        style,
                        className,
                        onChange = NullAsDefault,
                        getPreviewName = (name) => `${name}_preview`,
                        Wrapper = PhotoUploadWrapper,
                        holder = PhotoPlaceholderApp
                    }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const previewName = getPreviewName(name)

    const previewImage = _.get(values, previewName, value)

    return (
        <Wrapper>
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    minHeight: isSm ? 150 : '10vw',
                    maxHeight: isSm ? 200 : '14vw',
                    ...(style || {}),
                }}
                className={classNames('border rounded-lg flex flex-center flex-1 p-3 overflow-hidden', className)}>
                {previewImage ?
                    <ImageLoader
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            background: '#f1f1f1',
                            borderRadius: '0.375rem',
                        }}
                        src={previewImage}
                        holder={holder}
                        alt="photo upload"
                    /> : <BsImage size={50} style={{color: '#f1f1f1'}}/>
                }
                <div className="flex flex-center gap-3 absolute bottom-5 right-5">
                    <ImageUpload
                        name={name}
                        value={value}
                        maxWidth={1920}
                        maxHeight={384}
                        onChange={onChange}
                        renderPreview={NullAsDefault}
                        showUploadList={false}
                        getPreviewName={getPreviewName}>
                        <Button
                            style={{width: 24, height: 24, borderRadius: 4}}
                            icon={<IoImage size={12}/>}
                            className="flex flex-center background hover:bg-primary-50 border text-color-200 opacity-50 hover:opacity-100 cursor-pointer"
                        />
                    </ImageUpload>
                    <div
                        style={{width: 22, height: 22, borderRadius: 4}}
                        className="flex flex-center background border border-primary-400 text-primary-400 opacity-50 hover:opacity-100 cursor-pointer"
                        onClick={() => {
                            onChange(createValue(name, null))
                            onChange(createValue(previewName, null))
                        }}>
                        <BiTrash size={12}/>
                    </div>
                </div>
            </div>
        </Wrapper>
    )
}

const defaultFieldNames = {
    avatar: 'avatar',
    cover_photo: 'cover_photo',
    avatar_preview: 'avatar_preview',
    cover_photo_preview: 'cover_photo_preview',
}

export const PhotoUploadComposite = ({
                                         title,
                                         values,
                                         onChange,
                                         renderAvatar = renderSelf,
                                         avatar,
                                         cover_photo = renderSelf,
                                         renderCoverUploader = renderSelf,
                                         renderAvatarUploader = renderSelf,
                                         fieldNames = defaultFieldNames,
                                         fallback_cover = PhotoPlaceholderApp,
                                     }) => {
    const t = useTranslate()
    const cover_photo_preview = _.get(values, fieldNames.cover_photo_preview, cover_photo)

    return (
        <div style={{zIndex: 11}} className="w-full relative overflow-hidden">
            <div
                className="w-full relative overflow-hidden rounded-md"
                style={{
                    height: '200px',
                    background: 'var(--background)',
                }}>
                <div
                    key={cover_photo_preview}
                    className="flex"
                    style={{
                        top: 0,
                        left: 0,
                        position: 'absolute',
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        background: 'var(--background)',
                        backgroundImage: `url(${cover_photo_preview ?? fallback_cover})`,
                        backgroundSize: 'cover',
                        backgroundPosition: '50% 50%',
                    }}
                />
                <div
                    className="w-full absolute top-0 left-0"
                    style={{
                        height: '200px',
                        objectFit: 'cover',
                    }}
                />
                <div
                    className="w-full flex flex-col gap-3 absolute bottom-0 left-0"
                    style={{padding: '0.9em'}}>
                    {renderAvatar(
                        <Avatar
                            size={80}
                            className="border-2 border-color-50 rounded-xl background-50"
                            src={_.get(values, fieldNames.avatar_preview, avatar)}
                            icon={<BsPersonFill className="text-color-400"/>}
                            shape="square"
                        />
                    )}
                    <Translate>
                        {(t) => (
                            <div className="flex justify-around">
                                {renderAvatarUploader(
                                    <div className="flex flex-center gap-3">
                                        <ImageUpload
                                            onChange={onChange}
                                            renderPreview={NullAsDefault}
                                            showUploadList={false}
                                            name={fieldNames.avatar}>
                                            <div
                                                className="flex items-center gap-2 px-3 py-1 rounded-lg btn-upload-img hover:btn-upload-img cursor-pointer">
                                                <BsCloudArrowUp size={20} className="text-white"/>
                                                <div className="flex flex-col leading-tight text-white">
                                                    <span className="font-medium text-xs">{t('avatar')}</span>
                                                    <span className="text-2xs font-light italic opacity-50">{'( 256 x 256 )'}</span>
                                                </div>
                                            </div>
                                        </ImageUpload>
                                        <div
                                            className="flex items-center gap-2 p-2 rounded-lg btn-upload-img hover:btn-upload-img cursor-pointer"
                                            onClick={() => {
                                                onChange(createValue('avatar', null))
                                                onChange(createValue('avatar_preview', null))
                                            }}>
                                            <BiTrash size={20}/>
                                        </div>
                                    </div>
                                )}
                                {renderCoverUploader(
                                    <div className="flex flex-center gap-3">
                                        <ImageUpload
                                            onChange={onChange}
                                            maxWidth={1920}
                                            maxHeight={384}
                                            renderPreview={NullAsDefault}
                                            showUploadList={false}
                                            name={fieldNames.cover_photo}>
                                            <div
                                                className="flex items-center gap-2 px-3 py-1 rounded-lg btn-upload-img hover:btn-upload-img cursor-pointer">
                                                <BsCloudArrowUp size={20} className="text-white"/>
                                                <div className="flex flex-col leading-tight text-white">
                        <span className="font-medium text-xs">{t('cover')}</span>
                                                    <span className="text-2xs font-light italic opacity-50">{'( 1280 x 256 )'}</span>
                                                </div>
                                            </div>
                                        </ImageUpload>
                                        <div
                                            className="flex items-center gap-2 p-2 rounded-lg btn-upload-img hover:btn-upload-img cursor-pointer"
                                            onClick={() => {
                                                onChange(createValue('cover_photo', null))
                                                onChange(createValue('cover_photo_preview', null))
                                            }}>
                                            <BiTrash size={20}/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Translate>
                </div>
            </div>
            <div className="my-3 px-3 py-2 flex flex-col border border-color-50 shadow-items rounded-xl">
                <div className="mb-2 pb-1 border-b border-indigo-400 font-semibold text-indigo-600 tracking-wide italic">
                    <GoDotFill size={10} className="mr-1"/>
                    {`${t('note')}: `}
                </div>
                <span className="ml-3 text-sm text-color-300 tracking-wider">
                    {`- ${t('for the best image quality, we recommend using images with a size of 256 × 256')}`}
                </span>
                <span className="ml-3 text-sm text-color-300 tracking-wider">
                    {`- ${t('to ensure the best visual quality, we recommend using images with a resolution of 1280 × 256 for banners')}`}
                </span>
            </div>
        </div>
    )
}

export const PhotoArrayUpload = ({
                                     values,
                                     onChange,
                                     slots = [],
                                     name: fieldName,
                                 }) => {
    const t = useTranslate()
    return (
        <div id="photos" className="flex flex-col relative space-y-3 py-3">
            <div className="flex flex-col gap-1">
                <div className="font-bold leading-snug">
                    {t('Pictures on the home page')}
                </div>
                <div className="text-xs text-color-500 italic leading-tight">
                    {t("The banner image will be placed on the company's homepage. Best view with 256 x 1280 pixels resolution. Image type: PNG, JPG.")}
                </div>
            </div>
            {Array.from(slots || []).map(({name, preview}, index) => {
                const previewPhoto = _.get(values, preview)
                return (
                    <div
                        key={index}
                        className="w-full relative background-100 overflow-hidden rounded-lg"
                        style={{height: '234px'}}>
                        {previewPhoto && (
                            <ImageLoader
                                style={{height: '234px'}}
                                prefixCls="flex"
                                className="w-full absolute background object-cover top-0 left-0 rounded-lg"
                                src={previewPhoto}
                            />
                        )}
                        <div className="flex flex-col h-full flex-center space-y-2 rounded-md cursor-pointer">
                            <div className="flex flex-center p-1 rounded-md custom-btn-trash">
                                <PiTrash
                                    size={20}
                                    onClick={() => {
                                        onChange(createValue(name, null))
                                        onChange(createValue(preview, null))
                                    }}
                                />
                            </div>
                            <ImageUpload
                                name={name}
                                showUploadList={false}
                                onChange={onChange}
                                renderPreview={NullAsDefault}
                                maxWidth={1280}
                                maxHeight={256}>
                                <Button className="flex items-center gap-2 button-rounded-lg no-border btn-upload-img">
                                    <PiCloudArrowUp size={20}/>
                                    <span className="font-medium text-sm flex items-center">
                    {t('add photo')} {index}
                                        <span className="ml-2 italic font-light text-xs">
                      ( 1280 x 256 )
                    </span>
                  </span>
                                </Button>
                            </ImageUpload>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default PhotoField
